import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Tooltip, Typography } from "antd";
import {
	CheckCircleFilled,
	CheckCircleOutlined,
	CheckOutlined,
	CheckSquareOutlined,
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
	TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
	canAmendSignOffNotes,
	canDeleteCriterion,
	canEditCriterion,
	canSignCriterion,
	canSignNow,
	getRtpRights,
	getUserName,
	isActivePhase,
	isPhaseLocked,
	isPlanReadOnly,
	metricCriterionMetDays,
	phaseDateRanges,
} from "@utils/rtp_utils";
import { rtpApi, runRtpWrite, useCoachList, useCurrentUserUuid, useRtpMetricOptions } from "./rtpHooks";
import { buildDetails, CriterionFormFields, formatStat } from "./CriterionForm";
import type { CriterionFormValues } from "./CriterionForm";
import type { RtpCriterion, RtpCriterionDetails, RtpMetricProgress, RtpPhase, RtpPlanDetail } from "./types";

const { TextArea } = Input;

interface CriteriaListProps {
	plan: RtpPlanDetail;
	phase: RtpPhase;
	refetch: () => void;
	progressByKey: Record<string, RtpMetricProgress>;
	onOpenProgress: (metricKey: string) => void;
}

function detailsChanged(criterion: RtpCriterion, nextDetails: RtpCriterionDetails): boolean {
	const prev = buildDetails(criterion.details, criterion.criterionType);
	const next = buildDetails(nextDetails, criterion.criterionType);
	return JSON.stringify(prev) !== JSON.stringify(next);
}

interface CriterionModalProps {
	plan: RtpPlanDetail;
	phase: RtpPhase;
	criterion: RtpCriterion | null;
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

const CriterionModal = ({ plan, phase, criterion, open, onClose, refetch }: CriterionModalProps) => {
	const coachList = useCoachList();
	const [form] = Form.useForm<CriterionFormValues>();
	const [submitting, setSubmitting] = useState(false);
	const criterionType = Form.useWatch("criterionType", form);
	const isEdit = criterion != null;

	useEffect(() => {
		if (!open) return;
		form.setFieldsValue(
			criterion
				? {
						criterionType: criterion.criterionType,
						description: criterion.details.description,
						metricSource: criterion.details.metricSource,
						metricKey: criterion.details.metricKey,
						comparison: criterion.details.comparison,
						threshold: criterion.details.threshold,
						allowedSigners: criterion.allowedSigners,
					}
				: {
						criterionType: "event",
						description: undefined,
						metricSource: undefined,
						metricKey: undefined,
						comparison: undefined,
						threshold: undefined,
						allowedSigners: [],
					}
		);
		// Re-seed only when the modal opens (or the target criterion changes) — not on every plan
		// refetch while it's already open, which would clobber in-progress edits.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, criterion]);

	const handleClose = () => {
		onClose();
		form.resetFields();
	};

	const submitWrite = async (write: () => Promise<unknown>) => {
		setSubmitting(true);
		const ok = await runRtpWrite(write, refetch);
		setSubmitting(false);
		if (ok) handleClose();
	};

	const handleSubmit = async () => {
		let values: CriterionFormValues;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}
		const details = buildDetails(values, values.criterionType);

		if (!criterion) {
			await submitWrite(() =>
				rtpApi.rtpPhaseAction(phase.id, {
					action: "ADD_CRITERION",
					criterionType: values.criterionType,
					details,
					allowedSigners: values.allowedSigners,
				})
			);
			return;
		}

		const doPatch = () =>
			submitWrite(() =>
				// NEVER send criterionType in a PATCH — it's fixed after create (§7).
				rtpApi.patchRtpCriterion(criterion.id, { details, allowedSigners: values.allowedSigners })
			);

		if (criterion.signedOffBy != null && detailsChanged(criterion, details)) {
			Modal.confirm({
				title: "Clear the existing sign-off?",
				content: `This edit clears the existing sign-off — ${getUserName(plan, criterion.signedOffBy)}'s signature and notes will be removed.`,
				okText: "Save & Clear Sign-off",
				okType: "danger",
				okButtonProps: { "data-attr": "settings.rtp-criterion.submit" } as any,
				onOk: doPatch,
			});
			return;
		}

		await doPatch();
	};

	return (
		<Modal
			className="rtp-wz rtp-crit-modal"
			width={640}
			title={isEdit ? "Edit Exit Criteria" : "Add Exit Criteria"}
			open={open}
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={submitting}
			destroyOnHidden
			okButtonProps={{ className: "rtp-wz-primary-btn", "data-attr": "settings.rtp-criterion.submit" } as any}
		>
			{/* Every visible field is required — the asterisks are noise (and the label-less
			    comparison column would render a floating lone asterisk). */}
			<Form form={form} layout="vertical" requiredMark={false}>
				<CriterionFormFields
					criterionType={criterionType}
					disableType={isEdit}
					coachList={coachList}
					player={{ id: plan.playerId, name: plan.playerName }}
					extraSignerOptions={(criterion?.allowedSigners ?? [])
						.filter((uuid) => !coachList.some((c) => c.uuid === uuid))
						.map((uuid) => ({ value: uuid, label: getUserName(plan, uuid) }))}
				/>
			</Form>
		</Modal>
	);
};

interface SignOffModalProps {
	criterion: RtpCriterion | null;
	mode: "sign-off" | "amend" | null;
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

interface SignOffFormValues {
	signedOffNotes?: string;
}

// One modal, one mode: sign-off posts the SIGN_OFF action (omitting signedOffNotes when blank);
// amend PATCHes signedOffNotes as the ONLY body key, even when the amended value is blank.
const SignOffModal = ({ criterion, mode, open, onClose, refetch }: SignOffModalProps) => {
	const [form] = Form.useForm<SignOffFormValues>();
	const [submitting, setSubmitting] = useState(false);
	const isAmend = mode === "amend";

	useEffect(() => {
		if (!open) return;
		form.setFieldsValue({ signedOffNotes: isAmend ? (criterion?.signedOffNotes ?? "") : undefined });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, criterion, mode]);

	const handleClose = () => {
		onClose();
		form.resetFields();
	};

	const handleSubmit = async () => {
		if (!criterion) return;
		let values: SignOffFormValues;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}
		setSubmitting(true);
		const notes = (values.signedOffNotes ?? "").trim();
		const write = isAmend
			? () => rtpApi.patchRtpCriterion(criterion.id, { signedOffNotes: notes })
			: () =>
					rtpApi.rtpCriterionAction(criterion.id, {
						action: "SIGN_OFF",
						...(notes ? { signedOffNotes: notes } : {}),
					});
		const ok = await runRtpWrite(write, refetch);
		setSubmitting(false);
		if (ok) handleClose();
	};

	return (
		<Modal
			className="rtp-wz rtp-crit-modal"
			title={isAmend ? "Amend Sign-off Notes" : "Sign Off Exit Criteria"}
			open={open}
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={submitting}
			destroyOnHidden
			okButtonProps={{ className: "rtp-wz-primary-btn", "data-attr": "settings.rtp-criterion.sign-off-submit" } as any}
		>
			<Form form={form} layout="vertical">
				<Form.Item name="signedOffNotes" label="Notes (optional)">
					<TextArea rows={3} placeholder="Optional notes" />
				</Form.Item>
			</Form>
		</Modal>
	);
};

export const CriteriaList = ({ plan, phase, refetch, progressByKey, onOpenProgress }: CriteriaListProps) => {
	const currentUserUuid = useCurrentUserUuid();
	const { byKey: metricByKey } = useRtpMetricOptions();
	const [criterionModal, setCriterionModal] = useState<{ criterion: RtpCriterion | null } | null>(null);
	const [signOffModal, setSignOffModal] = useState<{ criterion: RtpCriterion; mode: "sign-off" | "amend" } | null>(
		null
	);

	const openAdd = () => setCriterionModal({ criterion: null });
	const openEdit = (criterion: RtpCriterion) => setCriterionModal({ criterion });
	const closeCriterionModal = () => setCriterionModal(null);

	const openSignOff = (criterion: RtpCriterion) => setSignOffModal({ criterion, mode: "sign-off" });
	const openAmend = (criterion: RtpCriterion) => setSignOffModal({ criterion, mode: "amend" });
	const closeSignOffModal = () => setSignOffModal(null);

	const handleDelete = (criterion: RtpCriterion) => {
		Modal.confirm({
			title: "Delete this exit criteria?",
			content: "This can't be undone.",
			okText: "Delete",
			okType: "danger",
			okButtonProps: { "data-attr": "settings.rtp-criterion.delete-submit" } as any,
			onOk: () => runRtpWrite(() => rtpApi.deleteRtpCriterion(criterion.id), refetch),
		});
	};

	const showAdd =
		getRtpRights(plan, currentUserUuid).canCreateExitCriteria && !isPlanReadOnly(plan) && !isPhaseLocked(phase);

	// A reminder, not an auto sign-off: an unsigned metric criteria shows the first day in this
	// phase on which its threshold was met. Only for a phase that has started (active or
	// completed): a future phase has projected dates, and the current phase's data would fall
	// inside them. No phase dates yet (draft) → no range, no reminder.
	const phaseStarted = isActivePhase(plan, phase) || isPhaseLocked(phase);
	const phaseRange = phaseStarted ? phaseDateRanges(plan).find((r) => r.phase.id === phase.id) : undefined;
	const metDaysFor = (criterion: RtpCriterion) => {
		const progress = progressByKey[criterion.details.metricKey ?? ""];
		if (criterion.criterionType !== "metric" || criterion.signedOffBy != null || !phaseRange || !progress) return [];
		return metricCriterionMetDays(criterion.details, progress.dailyValues, phaseRange.start, phaseRange.end);
	};

	return (
		<div>
			<div className="rtp-crit-header">
				<span className="rtp-crit-header-label">
					<CheckSquareOutlined /> Exit Criteria
				</span>
				{showAdd ? (
					<Button
						size="small"
						type="dashed"
						icon={<PlusOutlined />}
						style={{ marginLeft: "auto" }}
						data-attr="settings.rtp-criterion.add"
						onClick={openAdd}
					>
						Add exit criteria
					</Button>
				) : null}
			</div>

			{phase.criteria.length === 0 ? <div className="rtp-crit-empty">No exit criteria yet.</div> : null}
			{phase.criteria.map((criterion) => {
				const signed = criterion.signedOffBy != null;
				const metDays = metDaysFor(criterion);
				const canSign = canSignCriterion(plan, phase, criterion, currentUserUuid);
				const showSign = !signed && canSignNow(plan, phase);
				const showAmend = canAmendSignOffNotes(phase, criterion, currentUserUuid) && !isPlanReadOnly(plan);
				const showEdit =
					canEditCriterion(plan, criterion, currentUserUuid) && !isPhaseLocked(phase) && !isPlanReadOnly(plan);
				const showDelete =
					canDeleteCriterion(plan, criterion, currentUserUuid) && !isPhaseLocked(phase) && !isPlanReadOnly(plan);

				const signersTooltip = (
					<div>
						<div style={{ fontWeight: 600, marginBottom: 2 }}>Staff allowed to sign off</div>
						{criterion.allowedSigners.map((uuid) => (
							<div key={uuid}>{getUserName(plan, uuid)}</div>
						))}
					</div>
				);

				return (
					<div key={criterion.id} className={`rtp-crit-row${signed ? " rtp-crit-row--signed" : ""}`}>
						<div className="rtp-crit-check">{signed ? <CheckOutlined /> : null}</div>
						<div className="rtp-crit-main">
							<div className="rtp-crit-title">
								<span>{criterion.details.description}</span>
								{criterion.criterionType === "metric" ? (
									<span className="rtp-crit-metric-chip">
										{metricByKey[criterion.details.metricKey ?? ""]?.label ?? criterion.details.metricKey}{" "}
										{criterion.details.comparison === "<=" ? "≤" : "≥"}{" "}
										{criterion.details.threshold != null ? formatStat(criterion.details.threshold) : ""}
									</span>
								) : null}
							</div>
							{signed ? (
								<div className="rtp-crit-signed-line">
									<CheckCircleFilled />
									Signed off by {getUserName(plan, criterion.signedOffBy)} ·{" "}
									{dayjs(criterion.signedOffAt).tz("America/New_York").format("MMM D, YYYY h:mm A z")}
								</div>
							) : null}
							{criterion.signedOffNotes ? <div className="rtp-crit-notes">“{criterion.signedOffNotes}”</div> : null}
							{metDays.length > 0 ? (
								<div className="rtp-crit-met-line">
									<CheckCircleOutlined />
									<span>
										Threshold met on {dayjs(metDays[0].date).format("MMM D")} ({formatStat(metDays[0].value)}).
										Check the progress{" "}
										<Typography.Link
											className="rtp-crit-met-link"
											data-attr="settings.rtp-criterion.open-progress"
											onClick={() => onOpenProgress(criterion.details.metricKey ?? "")}
										>
											here
										</Typography.Link>
									</span>
								</div>
							) : null}
						</div>
						<div className="rtp-crit-actions">
							{showSign ? (
								<Tooltip title={signersTooltip}>
									<span>
										<Button
											type="primary"
											size="small"
											disabled={!canSign}
											data-attr="settings.rtp-criterion.sign-off"
											onClick={() => openSignOff(criterion)}
										>
											Sign off
										</Button>
									</span>
								</Tooltip>
							) : null}
							{!signed && !showSign ? (
								<Tooltip title={signersTooltip}>
									<span className="rtp-crit-signers-hint">
										<TeamOutlined />
										{criterion.allowedSigners.length}
									</span>
								</Tooltip>
							) : null}
							{showAmend ? (
								<Button
									size="small"
									data-attr="settings.rtp-criterion.amend-notes"
									onClick={() => openAmend(criterion)}
								>
									Amend notes
								</Button>
							) : null}
							{showEdit ? (
								<Tooltip title="Edit">
									<Button
										type="text"
										size="small"
										icon={<EditOutlined />}
										aria-label="Edit exit criteria"
										data-attr="settings.rtp-criterion.edit"
										onClick={() => openEdit(criterion)}
									/>
								</Tooltip>
							) : null}
							{showDelete ? (
								<Tooltip title="Delete">
									<Button
										type="text"
										danger
										size="small"
										icon={<DeleteOutlined />}
										aria-label="Delete exit criteria"
										data-attr="settings.rtp-criterion.delete"
										onClick={() => handleDelete(criterion)}
									/>
								</Tooltip>
							) : null}
						</div>
					</div>
				);
			})}

			<CriterionModal
				plan={plan}
				phase={phase}
				criterion={criterionModal?.criterion ?? null}
				open={criterionModal !== null}
				onClose={closeCriterionModal}
				refetch={refetch}
			/>
			<SignOffModal
				criterion={signOffModal?.criterion ?? null}
				mode={signOffModal?.mode ?? null}
				open={signOffModal !== null}
				onClose={closeSignOffModal}
				refetch={refetch}
			/>
		</div>
	);
};
